// Typed client for /api/allergies. Mirrors the api package's
// CreateAllergySchema + DELETE /:id surface.
//
// V1 has no PATCH for allergies: edits flow as delete + re-create.
// This matches how the medications surface is built and keeps the
// audit trail simple ("user deleted X, user added Y") instead of
// silent UPDATE.

import { useCallback } from 'react';
import { useAuthedFetch } from '../hooks/useAuthedFetch';

export type AllergySeverity = 'MILD' | 'MODERATE' | 'SEVERE' | 'ANAPHYLAXIS';

export interface AllergyRow {
  id: string;
  allergen: string;
  allergenClass: string | null;
  reaction: string | null;
  severity: AllergySeverity;
  createdAt: string;
}

export interface CreateAllergyInput {
  allergen: string;
  /** Cross-react group id (penicillin, sulfa, nsaid, ace-inhibitor,
   *  statin, opioid-codeine). Optional but recommended — the engine
   *  uses substring matching on `allergen` if missing, so typing
   *  "PCN" without setting `allergenClass: 'penicillin'` still works
   *  but is less precise. */
  allergenClass?: string;
  reaction?: string;
  severity?: AllergySeverity;
}

interface ApiSuccess<T> {
  ok: true;
  data: T;
}
interface ApiError {
  ok: false;
  error: { code: string; message: string };
}

export function useAllergiesApi() {
  const authedFetch = useAuthedFetch();
  return {
    list: useCallback(async (): Promise<AllergyRow[]> => {
      const res = await authedFetch('/api/allergies');
      const body = (await res.json()) as ApiSuccess<{ items: AllergyRow[] }> | ApiError;
      if (!body.ok) throw new Error(body.error.message);
      return body.data.items;
    }, [authedFetch]),

    create: useCallback(
      async (input: CreateAllergyInput): Promise<AllergyRow> => {
        const res = await authedFetch('/api/allergies', {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const body = (await res.json()) as ApiSuccess<AllergyRow> | ApiError;
        if (!body.ok) throw new Error(body.error.message);
        return body.data;
      },
      [authedFetch],
    ),

    remove: useCallback(
      async (id: string): Promise<void> => {
        const res = await authedFetch(`/api/allergies/${id}`, { method: 'DELETE' });
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ApiError | null;
          throw new Error(body?.error.message ?? `HTTP ${res.status}`);
        }
      },
      [authedFetch],
    ),
  };
}

// ---------- Precheck (separate API, /api/clinical/allergy-precheck) ----------

export interface AllergyPrecheckMatch {
  groupId: string;
  groupLabel: string;
  allergen: string;
  severity: 'CRITICAL' | 'MAJOR' | 'MODERATE' | 'MINOR';
  explanation: string;
  citation: string;
}

export interface AllergyPrecheckInput {
  ingredientName?: string;
  displayName?: string;
  drugClasses?: string[];
}

export function useAllergyPrecheck() {
  const authedFetch = useAuthedFetch();
  return useCallback(
    async (input: AllergyPrecheckInput): Promise<AllergyPrecheckMatch[]> => {
      const res = await authedFetch('/api/clinical/allergy-precheck', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      const body = (await res.json()) as ApiSuccess<{ matches: AllergyPrecheckMatch[] }> | ApiError;
      if (!body.ok) throw new Error(body.error.message);
      return body.data.matches;
    },
    [authedFetch],
  );
}
